import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from "@auth0/auth0-react";
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { FaDollarSign, FaEnvelope, FaUser, FaFileAlt, FaClock, FaPaperPlane, FaComment, FaTimes } from 'react-icons/fa';

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #10b981;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #fff;
  }
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    font-size: 12px;
    color: rgba(99, 102, 241, 0.8);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$cols || '1fr 1fr'};
  gap: 12px;
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const AmountInput = styled.div`
  display: flex;
  
  .currency {
    padding: 12px 14px;
    background: rgba(99, 102, 241, 0.15);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-right: none;
    border-radius: 8px 0 0 8px;
    color: #6366f1;
    font-weight: 600;
    font-size: 14px;
    min-width: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  input {
    flex: 1;
    border-radius: 0 8px 8px 0;
    font-size: 16px;
    font-weight: 500;
  }
`;

const Select = styled.select`
  padding: 12px 14px;
  background: rgba(15, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  
  option {
    background: #1a1f2e;
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(99, 102, 241, 0.05);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }
  
  input {
    width: 18px;
    height: 18px;
    accent-color: #6366f1;
  }
  
  span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const Footer = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SendButton = styled(Button)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  color: #fff;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }
`;

const StatusMessage = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  background: ${props => props.$error 
    ? 'rgba(239, 68, 68, 0.1)' 
    : 'rgba(16, 185, 129, 0.1)'};
  border: 1px solid ${props => props.$error 
    ? 'rgba(239, 68, 68, 0.3)' 
    : 'rgba(16, 185, 129, 0.3)'};
  color: ${props => props.$error ? '#ef4444' : '#10b981'};
`;

const SuccessView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
  text-align: center;
  
  .icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #fff;
  }
  
  h3 {
    margin: 0;
    font-size: 18px;
    color: #fff;
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    max-width: 320px;
  }
  
  .code {
    background: rgba(99, 102, 241, 0.15);
    padding: 10px 20px;
    border-radius: 8px;
    font-family: monospace;
    font-size: 16px;
    color: #6366f1;
    font-weight: 600;
    letter-spacing: 1px;
  }
`;

const SERVICE_TYPES = [
  'Consultation',
  'Document Review',
  'Case Assessment',
  'Filing Fee',
  'Legal Services',
  'Meeting',
  'Other'
];

const PaymentRequestForm = ({ 
  isOpen, 
  onClose, 
  conversationId, 
  clientInfo: initialClientInfo,
  onPaymentCreated 
}) => {
  const { user } = useAuth0();
  const axiosInstance = useAuthenticatedAxios();
  
  const [formData, setFormData] = useState({
    clientName: initialClientInfo?.name || '',
    clientEmail: initialClientInfo?.email || '',
    clientPhone: initialClientInfo?.phone || '',
    amount: '',
    currency: 'USD',
    description: '',
    serviceType: 'Consultation',
    expirationDays: 7,
    expertNotes: '',
    sendEmail: true,
    sendToChat: !!conversationId
  });
  
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.clientName.trim()) {
      setError('Client name is required');
      return;
    }
    if (!formData.clientEmail.trim()) {
      setError('Client email is required');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    try {
      setSending(true);
      setError('');

      const response = await axiosInstance.post(`/client-payments/requests/${user.sub}`, {
        clientInfo: {
          name: formData.clientName,
          email: formData.clientEmail,
          phone: formData.clientPhone
        },
        conversationId: conversationId || undefined,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description,
        serviceType: formData.serviceType,
        expirationDays: formData.expirationDays,
        expertNotes: formData.expertNotes,
        sendEmail: formData.sendEmail,
        sendToChat: formData.sendToChat && conversationId
      });

      setSuccess(response.data.paymentRequest);
      
      if (onPaymentCreated) {
        onPaymentCreated(response.data.paymentRequest);
      }
    } catch (err) {
      console.error('Failed to create payment request:', err);
      setError(err.response?.data?.error || 'Failed to create payment request');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setFormData({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      amount: '',
      currency: 'USD',
      description: '',
      serviceType: 'Consultation',
      expirationDays: 7,
      expertNotes: '',
      sendEmail: true,
      sendToChat: false
    });
    setError('');
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>
            <FaDollarSign />
            Request Payment
          </Title>
          <CloseButton onClick={handleClose}>
            <FaTimes />
          </CloseButton>
        </Header>

        {success ? (
          <Body>
            <SuccessView>
              <div className="icon">✓</div>
              <h3>Payment Request Sent!</h3>
              <p>
                {formData.sendEmail 
                  ? `An email has been sent to ${formData.clientEmail} with payment instructions.`
                  : 'The payment request has been created.'}
              </p>
              <div className="code">{success.shortCode}</div>
              <p>Share this code or the payment link with your client.</p>
            </SuccessView>
            <Footer>
              <SendButton onClick={handleClose}>
                Done
              </SendButton>
            </Footer>
          </Body>
        ) : (
          <>
            <Body>
              {/* Client Info */}
              <Row>
                <InputGroup>
                  <Label><FaUser /> Client Name *</Label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.clientName}
                    onChange={(e) => handleChange('clientName', e.target.value)}
                  />
                </InputGroup>
                <InputGroup>
                  <Label><FaEnvelope /> Client Email *</Label>
                  <Input
                    type="email"
                    placeholder="client@email.com"
                    value={formData.clientEmail}
                    onChange={(e) => handleChange('clientEmail', e.target.value)}
                  />
                </InputGroup>
              </Row>

              {/* Amount */}
              <Row $cols="2fr 1fr">
                <InputGroup>
                  <Label><FaDollarSign /> Amount *</Label>
                  <AmountInput>
                    <span className="currency">{formData.currency}</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => handleChange('amount', e.target.value)}
                    />
                  </AmountInput>
                </InputGroup>
                <InputGroup>
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency}
                    onChange={(e) => handleChange('currency', e.target.value)}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="ILS">ILS</option>
                    <option value="CAD">CAD</option>
                  </Select>
                </InputGroup>
              </Row>

              {/* Service Type */}
              <Row>
                <InputGroup>
                  <Label><FaFileAlt /> Service Type</Label>
                  <Select
                    value={formData.serviceType}
                    onChange={(e) => handleChange('serviceType', e.target.value)}
                  >
                    {SERVICE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                </InputGroup>
                <InputGroup>
                  <Label><FaClock /> Expires In</Label>
                  <Select
                    value={formData.expirationDays}
                    onChange={(e) => handleChange('expirationDays', parseInt(e.target.value))}
                  >
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </Select>
                </InputGroup>
              </Row>

              {/* Description */}
              <InputGroup>
                <Label>Description *</Label>
                <TextArea
                  placeholder="What is this payment for? (e.g., Initial consultation for visa application)"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </InputGroup>

              {/* Delivery Options */}
              <Row>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={formData.sendEmail}
                    onChange={(e) => handleChange('sendEmail', e.target.checked)}
                  />
                  <span><FaEnvelope style={{ marginRight: 6 }} /> Send via Email</span>
                </CheckboxRow>
                {conversationId && (
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={formData.sendToChat}
                      onChange={(e) => handleChange('sendToChat', e.target.checked)}
                    />
                    <span><FaComment style={{ marginRight: 6 }} /> Send to Chat</span>
                  </CheckboxRow>
                )}
              </Row>

              {/* Error Message */}
              {error && (
                <StatusMessage $error>{error}</StatusMessage>
              )}
            </Body>

            <Footer>
              <CancelButton onClick={handleClose} disabled={sending}>
                Cancel
              </CancelButton>
              <SendButton onClick={handleSubmit} disabled={sending}>
                <FaPaperPlane />
                {sending ? 'Sending...' : 'Send Request'}
              </SendButton>
            </Footer>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default PaymentRequestForm;
